/**
 * Saved card ordering & status for My Account → Payments.
 * Merges `storedPaymentInstruments` + `invalidStoredPaymentInstruments` (SAVED_CARD),
 * then sorts: active → inactive → expired.
 */

const EXPIRED_STATUSES = new Set([
  'EXPIRED'
]);

const INACTIVE_STATUSES = new Set(['TOKEN_INVALID','TOKEN_EXPIRED']);

const DISPLAY_ORDER = { active: 0, inactive: 1, expired: 2 };

/**
 * @param {object} card — one entry from paymentInstrumentsInfo
 * @returns {'active' | 'inactive' | 'expired'}
 */
export function getCardDisplayCategory(card = {}) {
  const status = String(card.instrumentStatus || '')
    .trim()
    .toUpperCase();
  if (status && EXPIRED_STATUSES.has(status)) {
    return 'expired';
  }
  if (status && INACTIVE_STATUSES.has(status)) {
    return 'inactive';
  }
  return 'active';
}

export function sortSavedCardsByDisplayStatus(cards = []) {
  return [...cards].sort(
    (a, b) =>
      DISPLAY_ORDER[getCardDisplayCategory(a)] -
      DISPLAY_ORDER[getCardDisplayCategory(b)]
  );
}

/**
 * @param {object} paymentInfoSlice — Redux `state.paymentInfo`
 */
export function getMergedSavedCardsList(paymentInfoSlice = {}) {
  const root = paymentInfoSlice.paymentInfo || {};
  const storedList = root.storedPaymentInstruments || [];
  const invalidList = root.invalidStoredPaymentInstruments || [];

  const storedCards = storedList.find(
    i => i.paymentInstrumentCode === 'SAVED_CARD'
  );
  const invalidCards = invalidList.find(
    i => i.paymentInstrumentCode === 'SAVED_CARD'
  );

  const fromStored = storedCards?.paymentInstrumentsInfo || [];
  const fromInvalid = invalidCards?.paymentInstrumentsInfo || [];

  const byId = new Map();
  for (const c of fromStored) {
    if (c && c.paymentInstrumentId) {
      byId.set(c.paymentInstrumentId, { ...c });
    }
  }
  for (const c of fromInvalid) {
    if (!c || !c.paymentInstrumentId) continue;
    const prev = byId.get(c.paymentInstrumentId);
    byId.set(c.paymentInstrumentId, prev ? { ...prev, ...c } : { ...c });
  }

  return sortSavedCardsByDisplayStatus(Array.from(byId.values()));
}

function getStoredPaymentRoot(paymentInfoSlice = {}) {
  if (paymentInfoSlice.paymentInfo?.storedPaymentInstruments) {
    return paymentInfoSlice.paymentInfo;
  }
  return paymentInfoSlice;
}

/**
 * Integer count of SAVED_CARD instruments from payment-engine stored API
 * (`state.paymentInfo` / preloaded paymentInfo). Always an int, including 0.
 */
export function getCardsSavedCountFromPaymentInfo(paymentInfoSlice = {}) {
  const root = getStoredPaymentRoot(paymentInfoSlice);
  const storedList = root.storedPaymentInstruments || [];
  const storedCards = storedList.find(i => i && i.paymentInstrumentCode === 'SAVED_CARD');
  const cards = storedCards?.paymentInstrumentsInfo || [];
  const n = Array.isArray(cards) ? cards.length : 0;
  return Number.isFinite(n) ? n : 0;
}

export function getCardsSavedCountFromState(state = {}) {
  return getCardsSavedCountFromPaymentInfo(state?.paymentInfo || {});
}

export function getUpiIdSavedCountFromState(state = {}) {
  const root = getStoredPaymentRoot(state?.paymentInfo || {});
  const storedList = root.storedPaymentInstruments || [];
  const storedUpi = storedList.find(i => i && i.paymentInstrumentCode === 'SAVED_UPI');
  const upis = storedUpi?.paymentInstrumentsInfo || [];
  const n = Array.isArray(upis) ? upis.length : 0;
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {object} card
 * @param {'active' | 'inactive' | 'expired'} category
 */
export function getSavedCardAlertMessage(card = {}, category = 'active') {
  if (card.message) {
    return card.message;
  }
  if (category === 'expired') {
    return 'This card has expired. You can update it during checkout.';
  }
  if (category === 'inactive') {
    return "This card hasn't been used in a while. Update details during checkout.";
  }
  return null;
}
